import type { AllocationListItem, LabourItemListItem } from '@billda/api';
import type { WorkStatus } from '../../../shared/components/ledger';
import type { PlotLedgerSource } from '../hooks/useHierarchicalTableData';

/**
 * Maps the site's existing query tree onto ledger view models. It maps, it
 * does not aggregate: every money figure below comes from the API's own
 * financial metrics, and the only arithmetic is `target - booked` for the
 * subtotal strip, which the API already answers as `availableValue`.
 */

/** One booking against a labour item or a variation. */
export interface LedgerBooking {
  allocationId: string;
  status: WorkStatus;
  /** The allocation's unique reference. Null until the backend assigns one. */
  reference: string | null;
  bookedBy: string;
  bookedAt: string;
  amount: number;
  /** Admin cancel is refused once the booking has reached a pot. */
  hasPotSubmission: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedBy: string | null;
  rejectionReason: string | null;
}

export interface LedgerItemEntry {
  kind: 'item';
  key: string;
  labourItemId: number;
  title: string;
  /** What the item is worth. Null when the viewer cannot see money. */
  target: number | null;
  bookedAmount: number | null;
  remaining: number | null;
  allocationCount: number;
  bookings: LedgerBooking[];
  /** Bookings exist but have not arrived yet — the row renders with skeletons. */
  isLoadingBookings: boolean;
}

export interface LedgerVariationEntry {
  kind: 'variation';
  key: string;
  variationId: string;
  workTypeName: string;
  description: string;
  status: WorkStatus;
  amount: number;
  /** Stands in for the booking timestamp until the allocation arrives. */
  submittedAt: string;
  booking: LedgerBooking | null;
  isLoadingBooking: boolean;
}

export type LedgerEntry = LedgerItemEntry | LedgerVariationEntry;

export interface PlotLedgerGroup {
  key: string;
  plotId: number;
  /** "Plot 144". */
  label: string;
  /**
   * The plot's house type/name ("The Ashbury"). Null when the plot has no
   * name or the name just repeats the plot number.
   */
  secondaryLabel: string | null;
  progress: number | null;
  totalSpent: number | null;
  totalTarget: number | null;
  /** Nothing booked yet: the plot reads as the price book, not as a problem. */
  isTemplate: boolean;
  itemCount: number | null;
  isExpanded: boolean;
  /** Expanded but the plot's children have not arrived. */
  isLoading: boolean;
  entries: LedgerEntry[];
}

function toBooking(allocation: AllocationListItem): LedgerBooking {
  const approval = allocation.approval;
  const approved = approval?.isApproved === true;
  const rejected = approval != null && !approval.isApproved;

  return {
    allocationId: allocation.id,
    status: allocation.status,
    reference: allocation.uniqueReference ?? null,
    bookedBy: allocation.worker.displayName,
    bookedAt: allocation.bookedAt,
    amount: allocation.amountClaimed,
    hasPotSubmission: allocation.hasPotSubmission,
    approvedBy: approved ? approval.reviewedByName : null,
    approvedAt: approved ? approval.reviewedAt : null,
    rejectedBy: rejected ? approval.reviewedByName : null,
    rejectionReason: rejected ? (approval.rejectionReason ?? null) : null,
  };
}

function toItemEntry(
  item: LabourItemListItem,
  allocations: AllocationListItem[] | undefined
): LedgerItemEntry {
  const metrics = item.financialMetrics;

  return {
    kind: 'item',
    key: `item-${item.id}`,
    labourItemId: item.id,
    title: item.definitionName,
    target: metrics?.price ?? null,
    bookedAmount: metrics?.bookedAmount ?? null,
    remaining: metrics?.availableValue ?? null,
    allocationCount: item.allocationCount,
    bookings: (allocations ?? []).map(toBooking),
    isLoadingBookings: item.allocationCount > 0 && allocations === undefined,
  };
}

function toVariationEntry(
  variation: NonNullable<PlotLedgerSource['variations']>[number],
  allocations: AllocationListItem[] | undefined
): LedgerVariationEntry {
  const allocation = allocations?.[0];

  return {
    kind: 'variation',
    key: `variation-${variation.id}`,
    variationId: variation.id,
    workTypeName: variation.workTypeName,
    description: variation.description,
    status: variation.status,
    amount: variation.requestedAmount,
    submittedAt: variation.submittedAt,
    booking: allocation ? toBooking(allocation) : null,
    isLoadingBooking:
      variation.allocationCount > 0 && allocations === undefined,
  };
}

function toEntries(source: PlotLedgerSource): LedgerEntry[] {
  const items = (source.labourItems ?? []).map(item =>
    toItemEntry(item, source.allocationsByLabourItem.get(item.id))
  );
  const variations = (source.variations ?? []).map(variation =>
    toVariationEntry(variation, source.allocationsByVariation.get(variation.id))
  );
  return [...items, ...variations];
}

function toSecondaryLabel(plot: PlotLedgerSource['plot']): string | null {
  const name = plot.name?.trim();
  if (!name) {
    return null;
  }
  const lowered = name.toLowerCase();
  if (
    lowered === plot.plotNumber.toLowerCase() ||
    lowered === `plot ${plot.plotNumber}`.toLowerCase()
  ) {
    return null;
  }
  return name;
}

/**
 * G6: the plot list row carries no gang or labour group name — that is a
 * projection add on the backend, not something to derive here from another
 * endpoint. The house type/name IS on the row, so it rides as the band's
 * secondary label.
 */
export function toPlotLedgerGroups(
  sources: readonly PlotLedgerSource[]
): PlotLedgerGroup[] {
  return sources.map(source => {
    const metrics = source.plot.financialMetrics;
    const totalSpent = metrics?.totalSpent ?? null;

    return {
      key: `plot-${source.plot.id}`,
      plotId: source.plot.id,
      label: `Plot ${source.plot.plotNumber}`,
      secondaryLabel: toSecondaryLabel(source.plot),
      progress: metrics?.progress ?? null,
      totalSpent,
      totalTarget: metrics?.totalLabourPrice ?? null,
      isTemplate: totalSpent != null && totalSpent <= 0,
      itemCount: source.labourItems?.length ?? null,
      isExpanded: source.isExpanded,
      isLoading: source.isExpanded && source.labourItems === undefined,
      entries: source.isExpanded ? toEntries(source) : [],
    };
  });
}

/**
 * A booked item shows its subtotal strip unless a single booking already
 * covers the whole target — "£X of £Y" is banned from the columns, so the
 * strip is where the remainder is stated.
 */
export function needsSubtotalStrip(entry: LedgerItemEntry): boolean {
  if (entry.bookings.length === 0) {
    return false;
  }
  if (entry.target == null || entry.bookedAmount == null) {
    return false;
  }
  if (entry.bookings.length > 1) {
    return true;
  }
  return entry.remaining != null && entry.remaining > 0;
}
